import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Seo from "../../components/Seo";
import { FilePreview, PickerField, revoke, withPreview } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, lawyerService } from "../../services/lawyerApi";
import "../../components/lawyer/lawyer.css";

const MAX_ATTACHMENTS = 4;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toOptions = (arr = []) => arr.map((item) => ({ value: item.id, label: item.name }));
const byName = (arr = []) => arr.map((i) => ({ value: i.name, label: i.name }));

const EMPTY_FORM = { name: "", email: "", city: "", country: "", nationality: "", about: "" };

// Order the checks run in; the first failing field is scrolled into view.
const FIELD_ORDER = [
  "name", "email", "phone", "country", "city", "nationality", "languages", "jurisdictions",
  "arbitrators", "categories", "subCategories", "about", "picture", "certificate", "license", "terms",
];

const FieldError = ({ msg }) => (msg ? <small className="lwp-signup-error">{msg}</small> : null);

const LawyerSignup = () => {
  const navigate = useNavigate();
  const pictureRef = useRef(null);
  const certificateRef = useRef(null);
  const licenseRef = useRef(null);
  const attachmentRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [phone, setPhone] = useState({ value: "971", dialCode: "971" });
  const [languages, setLanguages] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [arbitrators, setArbitrators] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [picture, setPicture] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [license, setLicense] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [accepted, setAccepted] = useState(false);

  const [lookups, setLookups] = useState({ countries: [], languages: [], jurisdictions: [], arbitrators: [], categories: [] });
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.allSettled([lawyerService.getDropdownData(), lawyerService.getCategories()]).then(([dd, cats]) => {
      if (!active) return;
      const d = dd.status === "fulfilled" && dd.value?.data?.status ? dd.value.data.data : {};
      const c = cats.status === "fulfilled" && cats.value?.data?.status ? cats.value.data.data : [];
      setLookups({
        countries: byName(d.countries),
        languages: byName(d.languages),
        jurisdictions: toOptions(d.jurisdictions),
        arbitrators: toOptions(d.arbitrators),
        categories: toOptions(Array.isArray(c) ? c : c?.categories),
      });
      if (dd.status === "rejected" || cats.status === "rejected") toast.error("Some options couldn't be loaded.");
    });
    return () => {
      active = false;
    };
  }, []);

  // Sub-categories depend on the chosen categories; drop picks that no longer apply.
  const categoryKey = categories.join(",");
  useEffect(() => {
    if (!categories.length) {
      setSubCategoryOptions([]);
      setSubCategories([]);
      return undefined;
    }
    let active = true;
    lawyerService
      .getSubCategories(categories)
      .then((res) => {
        if (!active || !res?.data?.status) return;
        const data = res.data.data;
        const list = Array.isArray(data) ? data : data?.sub_categories || [];
        const valid = new Set(list.map((i) => i.id));
        setSubCategoryOptions(toOptions(list));
        setSubCategories((prev) => prev.filter((id) => valid.has(id)));
      })
      .catch(() => active && toast.error("Couldn't load sub categories."));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey]);

  // Release preview URLs of picked files when leaving the page.
  const filesRef = useRef([]);
  filesRef.current = [picture, certificate, license, ...attachments];
  useEffect(() => () => filesRef.current.forEach(revoke), []);

  const clearError = (key) => setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    clearError(key);
  };
  const pickValue = (key, setter) => (v) => {
    setter(v);
    clearError(key);
  };
  const setFormValue = (key) => pickValue(key, (v) => setForm((f) => ({ ...f, [key]: v })));

  const pickFile = (key, setter, { imageOnly } = {}) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (imageOnly && !file.type.startsWith("image/")) return toast.error("Please choose an image.");
    setter((prev) => {
      revoke(prev);
      return withPreview(file);
    });
    clearError(key);
  };

  const addAttachments = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_ATTACHMENTS - attachments.length;
    if (files.length > room) toast.info(`You can add up to ${MAX_ATTACHMENTS} attachments.`);
    setAttachments((prev) => [...prev, ...files.slice(0, room).map(withPreview)]);
  };

  const removeAttachment = (index) =>
    setAttachments((prev) => {
      revoke(prev[index]);
      return prev.filter((_, i) => i !== index);
    });

  const labelsFor = (options, ids) =>
    ids.map((id) => options.find((o) => o.value === id)?.label).filter(Boolean).join(", ");

  const writeWithAI = async () => {
    const text = form.about.trim();
    if (text.length < 10) return toast.error("Write a few words about yourself first, then let AI polish it.");
    setDrafting(true);
    try {
      const res = await lawyerService.draftBioWithAI(
        text,
        labelsFor(lookups.categories, categories),
        labelsFor(lookups.jurisdictions, jurisdictions)
      );
      if (!res?.data?.status) throw new Error(res?.data?.message);
      setForm((f) => ({ ...f, about: res.data.data.suggestion }));
      toast.success("Draft ready. Review and edit it before submitting.");
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "The AI assistant is unavailable right now."));
    } finally {
      setDrafting(false);
    }
  };

  const nationalNumber = phone.value.slice(phone.dialCode.length);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Full name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Please enter a valid email.";
    if (!nationalNumber) e.phone = "Phone number is required.";
    else if (nationalNumber.length < 6) e.phone = "Please enter a valid phone number.";
    if (!form.country) e.country = "Select your country.";
    if (!form.city.trim()) e.city = "City is required.";
    if (!form.nationality) e.nationality = "Select your nationality.";
    if (!languages.length) e.languages = "Select at least one language.";
    if (!jurisdictions.length) e.jurisdictions = "Select at least one jurisdiction.";
    if (lookups.arbitrators.length && !arbitrators.length) e.arbitrators = "Select at least one option.";
    if (!categories.length) e.categories = "Select at least one category.";
    if (subCategoryOptions.length && !subCategories.length) e.subCategories = "Select at least one sub category.";
    if (!form.about.trim()) e.about = "Tell clients a little about yourself.";
    if (!picture) e.picture = "Please upload your picture.";
    if (!certificate) e.certificate = "Required.";
    if (!license) e.license = "Required.";
    if (!accepted) e.terms = "Please accept the Privacy policy and Terms & conditions.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    const first = FIELD_ORDER.find((k) => e[k]);
    if (first) {
      toast.error(e[first]);
      document.querySelector(`[data-field="${first}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSaving(true);
    try {
      const email = form.email.trim();
      const res = await lawyerService.register({
        source: "web",
        type: "individual",
        name: form.name.trim(),
        email,
        phone: `+${phone.value}`,
        country: form.country,
        city: form.city.trim(),
        nationality: form.nationality,
        about: form.about.trim(),
        languages: JSON.stringify(languages),
        jurisdictions: JSON.stringify(jurisdictions),
        arbitrators: JSON.stringify(arbitrators),
        categories: JSON.stringify(categories),
        sub_categories: JSON.stringify(subCategories),
        picture: picture.file,
        certificate: certificate.file,
        license: license.file,
        attachments: attachments.map((a) => a.file),
      });
      if (!res?.data?.status) throw new Error(res?.data?.message);
      setDone({ email, message: res.data.message });
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to create your account."));
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="lwp lwp-auth">
        <Seo title="Account Created | Legal Platform" noindex />
        <div className="lwp-auth-card lwp-signup-done">
          <span className="lwp-signup-done-icon">
            <i className="bi bi-check-lg" />
          </span>
          <h1 className="lwp-auth-title">Account Created</h1>
          <p>
            {done.message ||
              "Thank you for registering. We'll review your documents and email you once your account is approved."}
          </p>
          <button
            type="button"
            className="lwp-btn"
            onClick={() => navigate("/lawyer/login", { state: { email: done.email } })}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lwp lwp-auth">
      <Seo title="Create Lawyer Account | Legal Platform" noindex />
      <div className="lwp-auth-card lwp-signup-card">
        <Link to="/lawyer/login" className="lwp-back" aria-label="Back to login">
          <i className="bi bi-chevron-left" />
        </Link>
        <h1 className="lwp-auth-title">Create Account</h1>

        <form className="lwp-signup-form" onSubmit={submit} noValidate>
          <div data-field="name">
            <input
              className={`lwp-input${errors.name ? " is-invalid" : ""}`}
              placeholder="Full Name*"
              value={form.name}
              onChange={set("name")}
              autoComplete="name"
            />
            <FieldError msg={errors.name} />
          </div>

          <div data-field="email">
            <input
              type="email"
              className={`lwp-input${errors.email ? " is-invalid" : ""}`}
              placeholder="Email*"
              value={form.email}
              onChange={set("email")}
              autoComplete="email"
            />
            <FieldError msg={errors.email} />
          </div>

          <div data-field="phone">
            <PhoneInput
              country="ae"
              value={phone.value}
              onChange={(value, data) => {
                setPhone({ value, dialCode: data?.dialCode || "" });
                clearError("phone");
              }}
              countryCodeEditable={false}
              enableSearch
              searchPlaceholder="Search country..."
              placeholder="Phone Number*"
              containerClass={`lwp-phone${errors.phone ? " is-invalid" : ""}`}
              inputProps={{ name: "phone", autoComplete: "tel" }}
            />
            <FieldError msg={errors.phone} />
          </div>

          <div data-field="country">
            <PickerField
              hideLabel
              label="Country"
              placeholder="Country*"
              options={lookups.countries}
              value={form.country}
              onChange={setFormValue("country")}
              invalid={!!errors.country}
            />
            <FieldError msg={errors.country} />
          </div>

          <div data-field="city">
            <input
              className={`lwp-input${errors.city ? " is-invalid" : ""}`}
              placeholder="City*"
              value={form.city}
              onChange={set("city")}
              autoComplete="address-level2"
            />
            <FieldError msg={errors.city} />
          </div>

          <div data-field="nationality">
            <PickerField
              hideLabel
              label="Nationality"
              placeholder="Nationality*"
              options={lookups.countries}
              value={form.nationality}
              onChange={setFormValue("nationality")}
              invalid={!!errors.nationality}
            />
            <FieldError msg={errors.nationality} />
          </div>

          <div data-field="languages">
            <PickerField
              hideLabel
              multi
              label="Language"
              placeholder="Language*"
              options={lookups.languages}
              value={languages}
              onChange={pickValue("languages", setLanguages)}
              invalid={!!errors.languages}
            />
            <FieldError msg={errors.languages} />
          </div>

          <div data-field="jurisdictions">
            <PickerField
              hideLabel
              multi
              label="Select Jurisdiction"
              placeholder="Select Jurisdiction*"
              options={lookups.jurisdictions}
              value={jurisdictions}
              onChange={pickValue("jurisdictions", setJurisdictions)}
              invalid={!!errors.jurisdictions}
            />
            <FieldError msg={errors.jurisdictions} />
          </div>

          <div data-field="arbitrators">
            <PickerField
              hideLabel
              multi
              label="Legal Consultant"
              placeholder="Legal Consultant*"
              options={lookups.arbitrators}
              value={arbitrators}
              onChange={pickValue("arbitrators", setArbitrators)}
              invalid={!!errors.arbitrators}
            />
            <FieldError msg={errors.arbitrators} />
          </div>

          <div data-field="categories">
            <PickerField
              hideLabel
              multi
              label="Select Categories"
              placeholder="Select Categories*"
              options={lookups.categories}
              value={categories}
              onChange={pickValue("categories", setCategories)}
              invalid={!!errors.categories}
            />
            <FieldError msg={errors.categories} />
          </div>

          <div data-field="subCategories">
            <PickerField
              hideLabel
              multi
              label="Select Sub Categories"
              placeholder="Select Sub Categories*"
              options={subCategoryOptions}
              value={subCategories}
              onChange={pickValue("subCategories", setSubCategories)}
              disabled={!categories.length}
              invalid={!!errors.subCategories}
            />
            <FieldError msg={errors.subCategories} />
          </div>

          <div data-field="about">
            <textarea
              className={`lwp-textarea lwp-signup-textarea${errors.about ? " is-invalid" : ""}`}
              placeholder="About Your Self*"
              value={form.about}
              onChange={set("about")}
              rows={4}
            />
            <FieldError msg={errors.about} />
            <button type="button" className="lwp-ai-btn lwp-signup-ai" onClick={writeWithAI} disabled={drafting}>
              {drafting ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-stars" />}
              Write with AI
            </button>
          </div>

          <div data-field="picture" className="lwp-signup-tile">
            <button
              type="button"
              className={`lwp-signup-upload-row${errors.picture ? " is-invalid" : ""}`}
              onClick={() => pictureRef.current?.click()}
            >
              <span className="lwp-signup-plus">
                {picture ? <img src={picture.src} alt="" /> : <i className="bi bi-plus-lg" />}
              </span>
              {picture ? "Change Your Picture" : "Upload Your Picture*"}
            </button>
            <input ref={pictureRef} type="file" accept="image/*" hidden onChange={pickFile("picture", setPicture, { imageOnly: true })} />
            <FieldError msg={errors.picture} />
          </div>

          <div className="lwp-signup-docs">
            {[
              { key: "certificate", label: "Certificate*", item: certificate, setter: setCertificate, ref: certificateRef },
              { key: "license", label: "Legal License*", item: license, setter: setLicense, ref: licenseRef },
            ].map(({ key, label, item, setter, ref }) => (
              <div key={key} data-field={key} className="lwp-signup-tile">
                <button
                  type="button"
                  className={`lwp-signup-doc${errors[key] ? " is-invalid" : ""}`}
                  onClick={() => ref.current?.click()}
                >
                  <span className="lwp-signup-doc-icon">
                    {item ? <FilePreview item={item} label={label} /> : <i className="bi bi-cloud-arrow-up" />}
                  </span>
                  {label}
                </button>
                <input ref={ref} type="file" accept="image/*,application/pdf" hidden onChange={pickFile(key, setter)} />
                <FieldError msg={errors[key]} />
              </div>
            ))}
          </div>

          <div className="lwp-signup-tile">
            {attachments.length > 0 && (
              <div className="lwp-signup-attachments">
                {attachments.map((item, i) => (
                  <div key={item.src} className="lwp-edit-attachment">
                    <FilePreview item={item} label={`Attachment ${i + 1}`} />
                    <button type="button" className="lwp-edit-attachment-x" onClick={() => removeAttachment(i)} aria-label="Remove attachment">
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {attachments.length < MAX_ATTACHMENTS && (
              <button type="button" className="lwp-signup-upload-row" onClick={() => attachmentRef.current?.click()}>
                <span className="lwp-signup-plus">
                  <i className="bi bi-plus-lg" />
                </span>
                Add More Detail
              </button>
            )}
            <input ref={attachmentRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={addAttachments} />
          </div>

          <div data-field="terms" className="lwp-signup-terms">
            <label>
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => {
                  setAccepted(e.target.checked);
                  clearError("terms");
                }}
              />
              <span>
                Accept all{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy policy
                </Link>{" "}
                and{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">
                  Terms &amp; conditions
                </Link>
                .
              </span>
            </label>
            <FieldError msg={errors.terms} />
          </div>

          <button type="submit" className="lwp-btn" disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm" /> : "Create Account"}
          </button>
        </form>

        <p className="lwp-auth-footer">
          Already have an account? <Link to="/lawyer/login">Login</Link>
        </p>
      </div>
    </div>
  );
};

export default LawyerSignup;
